import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../provider/authProvider";
import { tryRefreshToken, redirectToLogin } from "../api/base";
import IdleTimeoutDialog from "../components/IdleTimeoutDialog";

function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;

    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (!decoded.exp) return true;

    const BUFFER_MS = 60_000;
    return decoded.exp * 1000 < Date.now() + BUFFER_MS;
  } catch {
    return true;
  }
}

export const ProtectedRoute = () => {
  const { token, setToken } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      if (!token) {
        localStorage.clear();
        setStatus('unauthenticated');
        return;
      }

      if (!isTokenExpired(token)) {
        setStatus('authenticated');
        return;
      }

      const refreshed = await tryRefreshToken();
      if (cancelled) return;

      if (refreshed) {
        const newToken = localStorage.getItem('token');
        setToken(newToken);
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
      }
    }

    setStatus('checking');
    checkAuth();

    return () => { cancelled = true; };
  }, [token, location.pathname]);

  if (status === 'checking') return null;

  if (status === 'unauthenticated') {
    redirectToLogin();
    return null;
  }

  return (
    <>
      <IdleTimeoutDialog />
      <Outlet />
    </>
  );
};
